<h4>{{title}}</h4>
<form
    id="digitalPlanForm"
    name="digitalPlanForm"
    novalidate
    autocomplete="off"
    #digitalPlanForm="ngForm">
    <!--
        One declaration: the Yes/No, and the lot numbers it applies to once it is Yes. Declared here rather than repeated per
        question so every declaration behaves the same, and inside the form so each instance registers its controls with it.
    -->
    <ng-template
        #lotDeclaration
        let-declaration>
        <div class="col-xs-12">
            <div class="required-input">{{declaration.question}}</div>
            @for (option of yesNoOptions; track $index) {
                <label class="nlr-radio">
                    <input
                        type="radio"
                        [attr.id]="declaration.field + 'Declared' + option.value"
                        [name]="declaration.field + 'Declared'"
                        [attr.name]="declaration.field + 'Declared'"
                        [ngModel]="declaration.answer"
                        (ngModelChange)="declaration.onChangeAnswer(option.value)"
                        [value]="option.value"
                        [disabled]="readonly || validationInProgress()"
                        required />
                    <span>{{option.name}}</span>
                </label>
            }
        </div>
        @if (declaration.answer) {
            <div
                class="col-xs-12 declared-lots"
                [ngClass]="{'has-error': declaration.showMissing(declaredLots) || declaration.textInvalid() || declaration.error()}">
                <label
                    class="control-label required-input"
                    [attr.for]="declaration.field"
                    >{{declaration.fieldLabel}}</label
                >
                <input
                    class="form-control"
                    type="text"
                    [attr.id]="declaration.field"
                    [name]="declaration.field"
                    [attr.name]="declaration.field"
                    maxlength="255"
                    placeholder="Enter lot numbers or ranges e.g. 1, 3, 5-8"
                    title="Enter lot numbers or ranges e.g. 1, 3, 5-8"
                    [ngModel]="declaration.text"
                    (ngModelChange)="declaration.onChangeText($event)"
                    (blur)="declaration.onBlurText()"
                    [readonly]="readonly || validationInProgress()"
                    #declaredLots="ngModel" />
                @if (declaration.showMissing(declaredLots)) {
                    <div class="alert alert-danger">'{{declaration.fieldLabel}}' entry required.</div>
                }
                @if (declaration.textInvalid()) {
                    <div class="alert alert-danger">Enter lot numbers or ranges e.g. 1, 3, 5-8</div>
                }
                @if (declaration.error()) {
                    <div class="alert alert-danger">{{declaration.error()}}</div>
                }
            </div>
        }
    </ng-template>
    <div class="container-fluid">
        <div class="row">
            <div class="col-xs-12">
                <h5>Attachment details</h5>
                <div class="required-input">DWG file</div>
                @if (getDigitalPlanAttachment()) {
                    <div class="input-group">
                        <span class="input-group-addon">
                            <i class="nlrp-icon nlrp-icon-file"></i>
                        </span>
                        <input
                            id="dwgFileName"
                            class="form-control"
                            type="text"
                            [name]="'dwgFileName'"
                            [attr.name]="'dwgFileName'"
                            [ngModel]="getDigitalPlanAttachment().fileName"
                            [readonly]="true" />
                        <span class="input-group-btn">
                            <button
                                id="dwgFileNameClear"
                                class="btn btn-default"
                                (click)="confirmClearDwg()"
                                [disabled]="readonly || validationInProgress()">
                                <i class="nlrp-icon nlrp-icon-trash"></i>
                            </button>
                        </span>
                    </div>
                } @else {
                    <div class="input-group">
                        <span class="input-group-btn">
                            <button
                                id="dwgFilePrependSelect"
                                class="btn btn-default"
                                (click)="dwgFileInput.click()"
                                [disabled]="readonly">
                                <i class="nlrp-icon nlrp-icon-file"></i>
                            </button>
                        </span>
                        <input
                            id="dwgFile"
                            type="file"
                            class="form-control"
                            [name]="'dwgFile'"
                            [attr.name]="'dwgFile'"
                            (change)="onChangeDwg(dwgFileInput.files[0])"
                            [accept]="['.dwg']"
                            [disabled]="readonly"
                            [required]="!readonly"
                            #dwgFileInput />
                        <span class="input-group-btn">
                            @if (dwgFile) {
                                <button
                                    id="dwgFileClear"
                                    class="btn btn-default"
                                    (click)="confirmClearDwg()"
                                    [disabled]="readonly">
                                    <i class="nlrp-icon nlrp-icon-trash"></i>
                                </button>
                            } @else {
                                <button
                                    id="dwgFileAppendSelect"
                                    class="btn btn-default"
                                    (click)="dwgFileInput.click()"
                                    [disabled]="readonly">
                                    <i class="nlrp-icon nlrp-icon-paper-clip"></i>
                                </button>
                            }
                        </span>
                    </div>

                    @if (fileExtensionInvalid()) {
                        <div>
                            <div class="alert alert-danger">Invalid file extension. Only .dwg files can be uploaded.</div>
                        </div>
                    }

                    @if (maxFileSizeExceeded()) {
                        <div>
                            <div class="alert alert-danger">File size exceeds maximum upload size of 10MB.</div>
                        </div>
                    }
                }
            </div>
            <div class="col-xs-12">
                <div class="required-input">Region</div>
                @for (option of regionOptions; track $index) {
                    <label class="nlr-radio">
                        <input
                            type="radio"
                            name="regionType"
                            [(ngModel)]="metro"
                            [value]="option.value"
                            [disabled]="readonly || validationInProgress()"
                            required />
                        <span>{{option.name}}</span>
                    </label>
                }
            </div>
            @if (!readonly) {
                @if (isAffectedTenure() || isFormerTenure()) {
                    @if (tenureCount() === 0) {
                        <div class="col-xs-12">
                            <div class="alert alert-danger">
                                @if (isAffectedTenure()) {
                                    'Affected tenure' required.
                                } @else {
                                    'Former tenure' required.
                                }
                            </div>
                        </div>
                    } @else if (newLotCount() < minLotCount()) {
                        <div class="col-xs-12">
                            <div class="alert alert-danger">
                                The new lot count ({{newLotCount()}}) is less than the minimum lot count ({{minLotCount()}}).
                            </div>
                        </div>
                    }
                }

                @if (surveyDetailsApplicable()) {
                    @if (!ssaOptionSelected()) {
                        <div class="col-xs-12">
                            <div class="alert alert-danger">'Declared as special survey area' selection required.</div>
                        </div>
                    }

                    @if (!surveyMethodSelected()) {
                        <div class="col-xs-12">
                            <div class="alert alert-danger">'Survey method' selection required.</div>
                        </div>
                    }
                }

                @if (!projectedCoordinateSystemSelected()) {
                    <div class="col-xs-12">
                        <div class="alert alert-danger">'Projected coordinate system' selection required.</div>
                    </div>
                }
            }

            @if (getDigitalPlanAttachment()) {
                @if (supportsMeridians()) {
                    <div class="col-xs-12">
                        <div class="required-input">Does the plan contain any Meridians?</div>
                        @for (option of meridianOptions; track $index) {
                            <label class="nlr-radio">
                                <input
                                    type="radio"
                                    name="meridian"
                                    [(ngModel)]="getDigitalPlanAttachment().meridian"
                                    [value]="option.value"
                                    [disabled]="readonly || validationInProgress()"
                                    required />
                                <span>{{option.name}}</span>
                            </label>
                        }
                    </div>
                }
                @if (commonPropertyDeclaration.applicable()) {
                    <ng-container
                        [ngTemplateOutlet]="lotDeclaration"
                        [ngTemplateOutletContext]="{$implicit: commonPropertyDeclaration}" />
                }
                @if (vestingLotsApplicable()) {
                    <div class="col-xs-12">
                        <div class="required-input">Vesting lots created on the plan?</div>
                        @for (option of yesNoOptions; track $index) {
                            <label class="nlr-radio">
                                <input
                                    type="radio"
                                    [attr.id]="'hasVestingLots' + option.value"
                                    name="hasVestingLots"
                                    [attr.name]="'hasVestingLots'"
                                    [ngModel]="hasVestingLots()"
                                    (ngModelChange)="onChangeHasVestingLots(option.value)"
                                    [value]="option.value"
                                    [disabled]="readonly || validationInProgress()"
                                    required />
                                <span>{{option.name}}</span>
                            </label>
                        }
                    </div>
                }
                @if (vestingLotsApplicable() && hasVestingLots()) {
                    <div class="col-xs-12">
                        <table class="table table-striped vesting-lots">
                            <thead>
                                <tr class="default">
                                    <th>Lot number</th>
                                    <th>Purpose</th>
                                    <th>Description</th>
                                    <th>Statutory reference</th>
                                    <th>Comments</th>
                                    <th>&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (vestingLot of getDigitalPlanAttachmentVestingLots(); track $index) {
                                    <tr>
                                        <td>
                                            <input
                                                class="form-control"
                                                type="text"
                                                maxlength="20"
                                                [attr.id]="'vestingLotNumber' + $index"
                                                [name]="'vestingLotNumber' + $index"
                                                [attr.name]="'vestingLotNumber' + $index"
                                                [(ngModel)]="vestingLot.lotNumber"
                                                [readonly]="readonly || validationInProgress()"
                                                required />
                                        </td>
                                        <td>
                                            <ng-select
                                                class="nlr-ng-select"
                                                [searchable]="false"
                                                [clearable]="false"
                                                [disabled]="readonly || validationInProgress()"
                                                [attr.id]="'vestingLotPurpose' + $index"
                                                [name]="'vestingLotPurpose' + $index"
                                                placeholder="Select"
                                                [items]="vestingPurposeLookups"
                                                bindLabel="description"
                                                bindValue="code"
                                                [(ngModel)]="vestingLot.purpose"
                                                (change)="onChangeVestingLotPurpose(vestingLot)"
                                                required>
                                            </ng-select>
                                        </td>
                                        <td>
                                            <input
                                                class="form-control"
                                                type="text"
                                                maxlength="255"
                                                [attr.id]="'vestingLotDescription' + $index"
                                                [name]="'vestingLotDescription' + $index"
                                                [attr.name]="'vestingLotDescription' + $index"
                                                [(ngModel)]="vestingLot.description"
                                                [readonly]="readonly || vestingLot.purpose !== 'OTHER' || validationInProgress()"
                                                required />
                                        </td>
                                        <td>
                                            <input
                                                class="form-control"
                                                type="text"
                                                maxlength="255"
                                                [attr.id]="'vestingLotStatutoryReference' + $index"
                                                [name]="'vestingLotStatutoryReference' + $index"
                                                [attr.name]="'vestingLotStatutoryReference' + $index"
                                                [(ngModel)]="vestingLot.statutoryReference"
                                                [readonly]="readonly || vestingLot.purpose !== 'OTHER' || validationInProgress()"
                                                required />
                                        </td>
                                        <td>
                                            <textarea
                                                class="form-control"
                                                rows="1"
                                                maxlength="255"
                                                [attr.id]="'vestingLotComments' + $index"
                                                [name]="'vestingLotComments' + $index"
                                                [attr.name]="'vestingLotComments' + $index"
                                                [(ngModel)]="vestingLot.comments"
                                                [readonly]="readonly || validationInProgress()"></textarea>
                                        </td>
                                        <td>
                                            @if (!readonly) {
                                                <button
                                                    [attr.id]="'addVestingLot' + $index"
                                                    [name]="'addVestingLot' + $index"
                                                    class="nlr-btn-bg-transparent"
                                                    type="button"
                                                    title="Add vesting lot"
                                                    (click)="addVestingLot($index)"
                                                    [disabled]="validationInProgress()">
                                                    <i class="nlrp-icon nlrp-icon-add-circle"></i>
                                                </button>
                                                <button
                                                    [attr.id]="'removeVestingLot' + $index"
                                                    [name]="'removeVestingLot' + $index"
                                                    class="nlr-btn-bg-transparent"
                                                    type="button"
                                                    title="Remove vesting lot"
                                                    [ngStyle]="{visibility: getDigitalPlanAttachmentVestingLots().length > 1 ? 'visible' : 'hidden'}"
                                                    (click)="removeVestingLot($index)"
                                                    [disabled]="validationInProgress()">
                                                    <i class="nlrp-icon nlrp-icon-remove-circle"></i>
                                                </button>
                                            }
                                        </td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                }
                @for (declaration of acquisitionDeclarations; track declaration.field) {
                    @if (declaration.applicable()) {
                        <ng-container
                            [ngTemplateOutlet]="lotDeclaration"
                            [ngTemplateOutletContext]="{$implicit: declaration}" />
                    }
                }
            }
            <div class="col-xs-12">
                @if (getDigitalPlanAttachment()) {
                    <button
                        id="validate"
                        type="button"
                        class="btn btn-default nlr-btn-sm"
                        [name]="'validate'"
                        [attr.name]="'validate'"
                        [disabled]="saveAndValidateDisabled()"
                        (click)="saveAndValidate()">
                        Validate
                    </button>
                } @else {
                    <button
                        id="upload"
                        type="button"
                        [name]="'upload'"
                        [attr.name]="'upload'"
                        class="btn btn-default nlr-btn-sm"
                        [disabled]="uploadDwgDisabled()"
                        (click)="uploadDwg()">
                        Upload
                    </button>
                }
            </div>
            @if (!readonly) {
                @if (validationState.errorMessage) {
                    <div class="col-xs-12">
                        <div class="alert alert-danger">{{validationState.errorMessage}}</div>
                    </div>
                }
                @if (uploadDwgErrorMessage) {
                    <div class="col-xs-12">
                        <div class="alert alert-danger">{{uploadDwgErrorMessage}}</div>
                    </div>
                }
            }
        </div>
        <div class="validations-row row">
            <div class="col-xs-12">
                <h5>
                    Validations
                    @if (validationInProgress()) {
                        <i class="nlrp-icon nlrp-icon-refresh-reload refresh-animate"></i>
                    }
                </h5>
            </div>
            <div class="col-xs-12">
                <table class="table table-striped">
                    <thead>
                        <tr class="default">
                            <th>Type</th>
                            <th>Status</th>
                            <th>Message</th>
                            <th>&nbsp;</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (result of getValidationResults(); track $index) {
                            <tr>
                                <td>{{result.type}}</td>
                                <td>{{result.status}}</td>
                                <td>
                                    @for (message of result.messages; track $index) {
                                        <div>{{message}}</div>
                                    }
                                </td>
                                <td>
                                    @if (result.pass) {
                                        <span class="badge badge-success">{{result.pass}}</span>
                                    }
                                    @if (result.warn) {
                                        <span class="badge badge-warning">{{result.warn}}</span>
                                    }
                                    @if (result.fail) {
                                        <span class="badge badge-danger">{{result.fail}}</span>
                                    }
                                </td>
                                <td>
                                    @if (result.url) {
                                        <button
                                            [attr.id]="'viewReport' + $index"
                                            class="nlr-btn-bg-transparent"
                                            type="button"
                                            title="View report"
                                            (click)="viewReport(result.type, result.url)">
                                            <i class="nlrp-icon nlrp-icon-view-attachment"></i>
                                        </button>
                                    }
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
            @if (!readonly) {
                <div class="col-xs-12">
                    <button
                        id="save"
                        type="button"
                        class="btn btn-default nlr-btn-sm"
                        title="Save"
                        [name]="'save'"
                        [attr.name]="'save'"
                        (click)="save()">
                        Save
                    </button>
                    @if (
                        validationState.validation &&
                        validationState.validation.status !== 'Failure' &&
                        validationState.validation.status !== 'Success'
                    ) {
                        <button
                            id="cancel"
                            type="button"
                            [name]="'cancel'"
                            [attr.name]="'cancel'"
                            class="btn btn-default"
                            (click)="confirmCancel()">
                            Cancel Validations
                        </button>
                    }
                </div>
            }
        </div>
    </div>
</form>
